/**
 * tooltip.jsx — Accessible tooltip primitives built on @radix-ui/react-tooltip.
 *
 * Exports: TooltipProvider, Tooltip
 *
 *   TooltipProvider — Must wrap the entire app (or at least the subtree that uses
 *                     tooltips). Sets a 400ms open delay for all tooltips inside it.
 *                     Already placed at the root of App.js.
 *
 *   Tooltip         — Wraps a single trigger element and shows a floating content
 *                     bubble on hover/focus. The trigger element is passed as
 *                     props.children; the bubble text is passed as props.content.
 *                     Uses a Radix Portal so the bubble renders outside any
 *                     overflow-hidden ancestors.
 */
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

/**
 * TooltipProvider — Context wrapper required by all Tooltip instances.
 * Place once at the top of the component tree.
 *
 * @param {React.ReactNode} props.children - The subtree that will contain tooltips.
 */
export function TooltipProvider({ children }) {
  return (
    // delayDuration: ms before the tooltip opens after the user hovers.
    <TooltipPrimitive.Provider delayDuration={400}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

/**
 * Tooltip — Attaches a floating label to any single child element.
 *
 * @param {React.ReactNode} props.children       - The trigger element (must accept ref).
 * @param {React.ReactNode} props.content        - Text or JSX shown inside the bubble.
 * @param {'top'|'bottom'|'left'|'right'} [props.side='top'] - Which side the bubble appears on.
 */
export function Tooltip({ children, content, side = 'top' }) {
  return (
    <TooltipPrimitive.Root>
      {/* asChild: Radix merges its trigger props onto the child element
          rather than wrapping it in an extra DOM node. */}
      <TooltipPrimitive.Trigger asChild>
        {children}
      </TooltipPrimitive.Trigger>
      {/* Portal: renders the bubble outside the nearest stacking context
          to avoid being clipped by overflow-hidden parents. */}
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 px-2.5 py-1.5 text-xs bg-gray-900 dark:bg-gray-700 text-white rounded-md shadow-lg select-none animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        >
          {content}
          {/* Arrow pointing from the bubble toward the trigger element. */}
          <TooltipPrimitive.Arrow className="fill-gray-900 dark:fill-gray-700" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
